/**
 * Fiche d'évaluation élève — Session plateau CCS 2 Roues
 *
 * Route : /enseignant/eval-plateau
 *
 * Version interactive tactile pro de la fiche officielle (50 min max).
 * 8 sections + signatures + sauvegarde locale + impression PDF.
 *
 * Stockage : localStorage clé `c2r:eval-plateau:draft`
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ClipboardCheck,
  Activity,
  Target,
  ListChecks,
  BookOpen,
  Eye,
  Layers,
  PenSquare,
  Printer,
  Save,
  Trash2,
  Timer,
  ChevronDown,
  ChevronUp,
  Bike,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import SuggestionsBox from '../components/teacher/SuggestionsBox';
import LivretValidationsRecap from '../components/teacher/LivretValidationsRecap';
import { inferFromPlateau, getSousCompetencesLivret } from '../data/teacher/evalInference';
import { FicheCoursLink } from '../components/teacher/EvalFormControls';
import useSeo from '../hooks/useSeo';

const STORAGE_KEY = 'c2r:eval-plateau:draft';

const EMPTY = {
  // Header
  nomEleve: '',
  date: '',
  enseignant: '',
  motoUtilisee: '',
  niveauActuel: '', // 'debutant' | 'intermediaire' | 'avance'
  // 1. Évaluation statique
  competencesAbordees: [],
  experiencePassee: {}, // { velo: 'oui|non', scooter: ..., ... }
  utilisationEnvisagee: [],
  typeMoto: [],
  apprehension: {}, // { stress: 'faible|moyen|important', ... }
  observationsEnseignant: '',
  heuresPlateauPrec: '',
  // 2. Évaluation dynamique
  parcoursTravaille: [],
  // 3. Objectif
  objectif: '',
  // 4. Critères
  criteresRealises: {}, // { regardLoin: 'oui|non', ... }
  // 5. Cours + schéma
  pointsTheoriques: [],
  notesExplications: '',
  schemaDescription: '',
  // 6. Démo
  demoElements: {},
  observationsDemo: '',
  // 7. Mise en pratique progressive
  niveauAutonomie: {},
  difficultes: '',
  securiteRespectee: '', // 'oui|aameliorer'
  commentairesSecurite: '',
  // 8. Auto-évaluation élève
  ressentiEleve: {},
  ressentiPersonnel: '',
  // 9. Validations REMC séance (trace pédagogique)
  livretValidations: {}, // { 'C1-e': 'acquis' | 'en_cours' | 'non_acquis' }
};

function loadDraft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    return { ...EMPTY, ...JSON.parse(raw) };
  } catch {
    return EMPTY;
  }
}

/* ─── Sous-composants tactiles ─────────────────────────────────────── */

function TouchCheckbox({ checked, onChange, label, testid, extra }) {
  return (
    <label
      className={`flex items-center gap-2.5 p-2.5 rounded-xl border-2 cursor-pointer transition-all select-none ${
        checked
          ? 'bg-[#ea580c]/10 border-[#ea580c] text-foreground'
          : 'bg-background border-border hover:border-foreground/30'
      }`}
      data-testid={testid}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        className="data-[state=checked]:bg-[#ea580c] data-[state=checked]:border-[#ea580c]"
      />
      <span className="text-sm font-semibold leading-tight flex-1">{label}</span>
      {extra}
    </label>
  );
}

function TouchOuiNon({ value, onChange, label, testid }) {
  return (
    <div
      className="flex items-center justify-between gap-2 p-2 rounded-xl border border-border bg-background/40"
      data-testid={testid}
    >
      <span className="text-sm font-semibold flex-1">{label}</span>
      <div className="flex gap-1">
        {['oui', 'non'].map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onChange(value === v ? '' : v)}
            data-testid={`${testid}-${v}`}
            className={`px-3 py-1 rounded-lg text-[11px] font-black uppercase tracking-wider transition-colors ${
              value === v
                ? v === 'oui'
                  ? 'bg-[#16a34a] text-white'
                  : 'bg-[#cf2031] text-white'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

function TouchTriple({ value, onChange, options, label, testid }) {
  return (
    <div
      className="flex items-center justify-between gap-2 p-2 rounded-xl border border-border bg-background/40"
      data-testid={testid}
    >
      <span className="text-sm font-semibold flex-1">{label}</span>
      <div className="flex gap-1 flex-wrap justify-end">
        {options.map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => onChange(value === opt.v ? '' : opt.v)}
            data-testid={`${testid}-${opt.v}`}
            className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-colors ${
              value === opt.v ? 'text-white' : 'bg-muted text-muted-foreground'
            }`}
            style={value === opt.v ? { background: opt.color } : undefined}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Section({ n, icon: Icon, label, duree, color, children, testid }) {
  const [open, setOpen] = useState(true);
  return (
    <section
      className="rounded-2xl bg-card border border-border/60 overflow-hidden"
      style={{ borderLeft: `5px solid ${color}` }}
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-4 sm:p-5 text-left hover:bg-muted/30 transition-colors no-print"
        aria-expanded={open}
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-black text-sm flex-shrink-0"
          style={{ background: color }}
        >
          {n}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Icon className="w-4 h-4" style={{ color }} />
            <h3 className="text-sm sm:text-base font-black tracking-tight">{label}</h3>
            {duree && (
              <span
                className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: `${color}1a`, color }}
              >
                {duree}
              </span>
            )}
          </div>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>
      {/* Print : toujours visible */}
      <div className={`p-4 sm:p-5 pt-0 border-t border-border/40 bg-background/40 ${open ? '' : 'hidden print:block'}`}>
        {children}
      </div>
    </section>
  );
}

/* ─── Constantes des choix ─────────────────────────────────────────── */

/**
 * Compétences travaillables en Plateau = toute la C1 du livret REMC officiel
 * (12 sous-compétences, contrôles C1-b inclus).
 */
const COMPETENCES_PLATEAU = getSousCompetencesLivret({
  categories: ['C1'],
});

const EXPERIENCES = ['Vélo', 'Scooter', 'Moto 125', 'Gros cube', 'Tout-terrain'];

const UTILISATIONS = ['Travail', 'Loisir', 'Quotidien', 'Voyage', 'Permis uniquement'];

const TYPES_MOTO = ['Roadster', 'Trail', 'Sportive', 'Custom', 'Scooter', 'Touring'];

const APPREHENSIONS = [
  { key: 'stress', label: 'Stress / peur chute' },
  { key: 'equilibre', label: 'Gestion équilibre' },
  { key: 'allure', label: 'Gestion allure lente' },
  { key: 'regard', label: 'Regard / trajectoire' },
  { key: 'confiance', label: 'Confiance globale' },
];

const PARCOURS = ['Allure lente', 'Slalom', 'Demi-tour', 'Freinage', 'Évitement', 'Parcours complet'];

const CRITERES = [
  { key: 'regardLoin', label: 'Regard porté loin' },
  { key: 'positionAdaptee', label: 'Position adaptée' },
  { key: 'gestionAllure', label: 'Gestion allure' },
  { key: 'coordination', label: 'Coordination commandes' },
  { key: 'trajectoire', label: 'Trajectoire maîtrisée' },
  { key: 'stabilite', label: 'Stabilité moto' },
  { key: 'respectConsignes', label: 'Respect consignes' },
];

const POINTS_THEORIQUES = [
  'Regard', 'Équilibre', 'Embrayage', 'Frein arrière',
  'Contre-braquage', 'Trajectoire', 'Position corps', 'Sécurité',
];

const DEMO_ELEMENTS = [
  { key: 'complete', label: 'Démonstration complète' },
  { key: 'explications', label: 'Explications simultanées' },
  { key: 'erreurs', label: 'Mise en évidence erreurs fréquentes' },
  { key: 'decomposition', label: 'Décomposition gestes' },
];

const NIVEAUX_AUTONOMIE = [
  { key: 'guidee', label: 'Réalisation guidée' },
  { key: 'semiGuidee', label: 'Réalisation semi-guidée' },
  { key: 'autonome', label: 'Réalisation autonome' },
];

const RESSENTI_OPTS = [
  { key: 'comprendsExercice', label: "Je comprends l'exercice" },
  { key: 'progression', label: 'Je me sens en progression' },
  { key: 'maitrise', label: 'Je maîtrise mieux la moto' },
  { key: 'entrainer', label: "Je dois encore m'entraîner" },
];

const APPREHENSION_OPTS = [
  { v: 'faible', label: 'Faible', color: '#16a34a' },
  { v: 'moyen', label: 'Moyen', color: '#f59e0b' },
  { v: 'important', label: 'Important', color: '#cf2031' },
];

const NIVEAU_ACTUEL_OPTS = [
  { v: 'debutant', label: 'Débutant', color: '#0891b2' },
  { v: 'intermediaire', label: 'Intermédiaire', color: '#f59e0b' },
  { v: 'avance', label: 'Avancé', color: '#16a34a' },
];

/* ─── Page principale ──────────────────────────────────────────────── */

export default function EvalPlateauForm() {
  useSeo({
    title: "Fiche d'évaluation élève · Session plateau CCS 2 Roues",
    description:
      "Fiche d'évaluation interactive pour les sessions plateau CCS 2 Roues : 8 sections, 50 min max, sauvegarde locale, impression PDF.",
    path: '/enseignant/eval-plateau',
  });

  const [form, setForm] = useState(() => loadDraft());

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch { /* ignore */ }
  }, [form]);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const toggleArr = (field, value) => {
    setForm((f) => {
      const arr = f[field] || [];
      const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...f, [field]: next };
    });
  };
  const updateObj = (field, key, value) => {
    setForm((f) => ({ ...f, [field]: { ...(f[field] || {}), [key]: value } }));
  };

  // Validation enseignant REMC : statut séance pour chaque suggestion adoptée
  const handleValidateLivret = (code, status) => {
    setForm((f) => {
      const next = { ...(f.livretValidations || {}) };
      if (status === null || status === undefined) {
        delete next[code];
      } else {
        next[code] = status;
      }
      return { ...f, livretValidations: next };
    });
  };

  const progress = useMemo(() => {
    const filled = [
      form.nomEleve, form.objectif, form.observationsEnseignant,
      form.ressentiPersonnel, form.difficultes, form.notesExplications,
    ].filter((v) => v && v.trim()).length;
    const total = 6;
    return { filled, total, pct: Math.round((filled / total) * 100) };
  }, [form]);

  const handleReset = () => {
    if (window.confirm("Effacer toute la fiche en cours ?")) setForm(EMPTY);
  };
  const handlePrint = () => window.print();

  return (
    <div className="space-y-4" data-testid="eval-plateau-page">
      <Link
        to="/enseignant/terrain"
        className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground hover:text-foreground no-print"
      >
        <ArrowLeft className="w-4 h-4" /> Plateau
      </Link>

      {/* HEADER tactile pro */}
      <header
        className="relative overflow-hidden rounded-3xl border border-border/60 bg-card"
        style={{
          boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 16px 40px -20px rgba(30,64,175,0.25)',
        }}
        data-testid="eval-header"
      >
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: '#1d4ed8' }} aria-hidden="true" />
        <div className="relative p-5 sm:p-7">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-[#1d4ed8]/15 border border-[#1d4ed8]/30 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-[#1d4ed8] mb-2">
                <Bike className="w-3 h-3" /> CCS 2 Roues · Session plateau
              </div>
              <h1
                className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-[1.05]"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                Fiche d&apos;évaluation élève
              </h1>
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                <Timer className="w-3.5 h-3.5" /> Durée max <strong className="text-foreground">50 min</strong>
              </div>
            </div>
          </div>

          {/* Champs en-tête */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-black">Nom élève</Label>
              <Input value={form.nomEleve} onChange={(e) => update('nomEleve', e.target.value)} className="mt-1" data-testid="f-nom" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-black">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => update('date', e.target.value)} className="mt-1" data-testid="f-date" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-black">Enseignant</Label>
              <Input value={form.enseignant} onChange={(e) => update('enseignant', e.target.value)} className="mt-1" data-testid="f-enseignant" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-black">Moto utilisée</Label>
              <Input value={form.motoUtilisee} onChange={(e) => update('motoUtilisee', e.target.value)} className="mt-1" data-testid="f-moto" />
            </div>
          </div>

          {/* Niveau actuel */}
          <div className="mt-4">
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1.5">Niveau actuel estimé</Label>
            <div className="flex flex-wrap gap-1.5">
              {NIVEAU_ACTUEL_OPTS.map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => update('niveauActuel', form.niveauActuel === opt.v ? '' : opt.v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider transition-all ${
                    form.niveauActuel === opt.v ? 'text-white' : 'bg-muted text-muted-foreground'
                  }`}
                  style={form.niveauActuel === opt.v ? { background: opt.color } : undefined}
                  data-testid={`niveau-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Progress + actions */}
          <div className="mt-5 flex items-center gap-3 flex-wrap no-print">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="flex items-center justify-between text-[11px] mb-1">
                <span className="font-bold text-muted-foreground">Complétion</span>
                <span className="tabular-nums font-black text-[#1d4ed8]">{progress.pct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-[#1d4ed8] transition-all" style={{ width: `${progress.pct}%` }} />
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleReset} className="gap-1.5" data-testid="btn-reset">
              <Trash2 className="w-3.5 h-3.5" /> Vider
            </Button>
            <Button size="sm" onClick={handlePrint} className="gap-1.5 bg-[#1d4ed8]" data-testid="btn-print">
              <Printer className="w-4 h-4" /> Imprimer / PDF
            </Button>
          </div>
        </div>
      </header>

      {/* SECTION 1 — ÉVALUATION STATIQUE */}
      <Section n="1" icon={ClipboardCheck} label="Évaluation statique" duree="5 min" color="#0891b2" testid="section-1">
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-2">A · Présentation & Accueil — points à couvrir</Label>
            <p className="text-xs text-muted-foreground italic">Présentation séance · Vérification équipement EPI · Vérification administrative</p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">B · Nombre d&apos;heures plateau déjà effectuées</Label>
            <Input value={form.heuresPlateauPrec} onChange={(e) => update('heuresPlateauPrec', e.target.value)} placeholder="Ex : 4h" data-testid="f-heures" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Compétences livret REMC à travailler / abordées · C1 complète</Label>
            <p className="text-[10px] italic text-muted-foreground mb-2">Coche les sous-compétences C1 que tu prévois de travailler (ou déjà abordées). Source : Livret d&apos;apprentissage A officiel. Le badge « Fiche » ouvre la fiche cours à donner quand disponible.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {COMPETENCES_PLATEAU.map((sc) => (
                <TouchCheckbox
                  key={sc.code}
                  checked={(form.competencesAbordees || []).includes(sc.code)}
                  onChange={() => toggleArr('competencesAbordees', sc.code)}
                  label={`${sc.code} · ${sc.intitule}`}
                  testid={`comp-${sc.code}`}
                  extra={<FicheCoursLink code={sc.code} accent="#0891b2" />}
                />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-black block mb-2">C · Expérience passée 2 roues</Label>
              <div className="space-y-1.5">
                {EXPERIENCES.map((e) => (
                  <TouchOuiNon key={e} label={e} value={form.experiencePassee[e]} onChange={(v) => updateObj('experiencePassee', e, v)} testid={`exp-${e}`} />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-black block mb-2">D · Type de moto envisagé</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {TYPES_MOTO.map((t) => (
                  <TouchCheckbox key={t} checked={(form.typeMoto || []).includes(t)} onChange={() => toggleArr('typeMoto', t)} label={t} testid={`type-${t}`} />
                ))}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-2">Utilisation principale envisagée</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {UTILISATIONS.map((u) => (
                <TouchCheckbox key={u} checked={(form.utilisationEnvisagee || []).includes(u)} onChange={() => toggleArr('utilisationEnvisagee', u)} label={u} testid={`util-${u}`} />
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-2">E · Niveau d&apos;appréhension ressenti</Label>
            <div className="space-y-1.5">
              {APPREHENSIONS.map((a) => (
                <TouchTriple key={a.key} label={a.label} value={form.apprehension[a.key]} onChange={(v) => updateObj('apprehension', a.key, v)} options={APPREHENSION_OPTS} testid={`appr-${a.key}`} />
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">F · Observations enseignant</Label>
            <Textarea rows={3} value={form.observationsEnseignant} onChange={(e) => update('observationsEnseignant', e.target.value)} data-testid="f-obs" />
          </div>
        </div>
      </Section>

      {/* SECTION 2 — ÉVALUATION DYNAMIQUE */}
      <Section n="2" icon={Activity} label="Évaluation dynamique — Mise en place parcours" duree="10 min" color="#7c3aed" testid="section-2">
        <Label className="text-[10px] uppercase tracking-wider font-black block mb-2">Parcours travaillé</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
          {PARCOURS.map((p) => (
            <TouchCheckbox key={p} checked={(form.parcoursTravaille || []).includes(p)} onChange={() => toggleArr('parcoursTravaille', p)} label={p} testid={`parc-${p}`} />
          ))}
        </div>
      </Section>

      {/* SECTION 3 — OBJECTIF */}
      <Section n="3" icon={Target} label="Objectif de la séance" color="#cf2031" testid="section-3">
        {/* Suggestions auto issues de l'inférence */}
        <div className="mb-3">
          <SuggestionsBox
            suggestions={inferFromPlateau(form)}
            onAdoptObjectif={(text) => update('objectif', text)}
            onValidateLivret={handleValidateLivret}
            livretValidations={form.livretValidations}
            fichesBaseRoute="/enseignant/terrain"
            accent="#cf2031"
          />
        </div>
        <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Objectif proposé / formulé avec l&apos;élève (commence par « Savoir »)</Label>
        <Textarea
          rows={2}
          value={form.objectif}
          onChange={(e) => update('objectif', e.target.value)}
          placeholder="« Savoir... »"
          className="font-bold"
          data-testid="f-objectif"
        />
      </Section>

      {/* SECTION 4 — CRITÈRES DE RÉALISATION */}
      <Section n="4" icon={ListChecks} label="Critères de réalisation" color="#ea580c" testid="section-4">
        <div className="space-y-1.5">
          {CRITERES.map((c) => (
            <TouchOuiNon key={c.key} label={c.label} value={form.criteresRealises[c.key]} onChange={(v) => updateObj('criteresRealises', c.key, v)} testid={`crit-${c.key}`} />
          ))}
        </div>
      </Section>

      {/* SECTION 5 — COURS + SCHÉMA */}
      <Section n="5" icon={BookOpen} label="Cours + Schéma" duree="10 min" color="#16a34a" testid="section-5">
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-2">Points théoriques abordés</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {POINTS_THEORIQUES.map((p) => (
                <TouchCheckbox key={p} checked={(form.pointsTheoriques || []).includes(p)} onChange={() => toggleArr('pointsTheoriques', p)} label={p} testid={`pth-${p}`} />
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Notes / explications</Label>
            <Textarea rows={2} value={form.notesExplications} onChange={(e) => update('notesExplications', e.target.value)} data-testid="f-notes" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Description schéma parcours / trajectoire</Label>
            <Textarea rows={3} value={form.schemaDescription} onChange={(e) => update('schemaDescription', e.target.value)} placeholder="Décris le parcours dessiné, la trajectoire idéale..." data-testid="f-schema" />
          </div>
        </div>
      </Section>

      {/* SECTION 6 — DÉMO ENSEIGNANT */}
      <Section n="6" icon={Eye} label="Démonstration enseignant" duree="5 min" color="#1d4ed8" testid="section-6">
        <div className="space-y-4">
          <div className="space-y-1.5">
            {DEMO_ELEMENTS.map((d) => (
              <TouchOuiNon key={d.key} label={d.label} value={form.demoElements[d.key]} onChange={(v) => updateObj('demoElements', d.key, v)} testid={`demo-${d.key}`} />
            ))}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Observations démo</Label>
            <Textarea rows={2} value={form.observationsDemo} onChange={(e) => update('observationsDemo', e.target.value)} data-testid="f-obs-demo" />
          </div>
        </div>
      </Section>

      {/* SECTION 7 — MISE EN PRATIQUE PROGRESSIVE */}
      <Section n="7" icon={Layers} label="Mise en pratique progressive" duree="10 min" color="#9333ea" testid="section-7">
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-2">Niveau d&apos;autonomie atteint</Label>
            <div className="space-y-1.5">
              {NIVEAUX_AUTONOMIE.map((n) => (
                <TouchOuiNon key={n.key} label={n.label} value={form.niveauAutonomie[n.key]} onChange={(v) => updateObj('niveauAutonomie', n.key, v)} testid={`autono-${n.key}`} />
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Difficultés rencontrées</Label>
            <Textarea rows={2} value={form.difficultes} onChange={(e) => update('difficultes', e.target.value)} data-testid="f-diff" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-2">Sécurité respectée</Label>
            <div className="flex gap-1.5">
              {[
                { v: 'oui', label: 'Oui', color: '#16a34a' },
                { v: 'aameliorer', label: 'À améliorer', color: '#cf2031' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => update('securiteRespectee', form.securiteRespectee === opt.v ? '' : opt.v)}
                  className={`flex-1 px-3 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                    form.securiteRespectee === opt.v ? 'text-white' : 'bg-muted text-muted-foreground'
                  }`}
                  style={form.securiteRespectee === opt.v ? { background: opt.color } : undefined}
                  data-testid={`secu-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Commentaires sécurité</Label>
            <Textarea rows={2} value={form.commentairesSecurite} onChange={(e) => update('commentairesSecurite', e.target.value)} data-testid="f-secu" />
          </div>
        </div>
      </Section>

      {/* SECTION 8 — AUTO-ÉVALUATION ÉLÈVE */}
      <Section n="8" icon={PenSquare} label="Auto-évaluation élève" duree="3 min" color="#16a34a" testid="section-8">
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-2">L&apos;élève estime</Label>
            <div className="space-y-1.5">
              {RESSENTI_OPTS.map((r) => (
                <TouchOuiNon key={r.key} label={r.label} value={form.ressentiEleve[r.key]} onChange={(v) => updateObj('ressentiEleve', r.key, v)} testid={`ress-${r.key}`} />
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Ressenti personnel</Label>
            <Textarea rows={3} value={form.ressentiPersonnel} onChange={(e) => update('ressentiPersonnel', e.target.value)} placeholder="L'élève s'exprime librement..." data-testid="f-ressenti" />
          </div>
        </div>
      </Section>

      {/* RÉCAP VALIDATIONS REMC — visible écran + PDF */}
      <LivretValidationsRecap
        validations={form.livretValidations}
        accent="#1d4ed8"
        studentName={form.nomEleve}
        context="plateau"
      />

      {/* SIGNATURES */}
      <section className="rounded-2xl bg-card border border-border/60 p-5 sm:p-6" data-testid="signatures">
        <h3 className="text-sm font-black tracking-tight mb-3 flex items-center gap-2">
          <Save className="w-4 h-4 text-[#1d4ed8]" /> Signatures
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black">Signature élève</Label>
            <div className="mt-1 h-16 rounded-xl border-2 border-dashed border-border bg-muted/30 flex items-center justify-center text-[10px] text-muted-foreground italic">
              À signer après impression
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black">Signature enseignant</Label>
            <div className="mt-1 h-16 rounded-xl border-2 border-dashed border-border bg-muted/30 flex items-center justify-center text-[10px] text-muted-foreground italic">
              À signer après impression
            </div>
          </div>
        </div>
      </section>

      {/* CTA bas */}
      <div className="flex flex-wrap gap-2 no-print">
        <Button onClick={handlePrint} className="gap-1.5 bg-[#1d4ed8]" data-testid="bottom-print">
          <Printer className="w-4 h-4" /> Imprimer / PDF
        </Button>
        <Button variant="outline" onClick={handleReset} className="gap-1.5">
          <Trash2 className="w-4 h-4" /> Vider la fiche
        </Button>
      </div>
    </div>
  );
}
